import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { GraduationCap, BookOpen, Code2, Wrench, ArrowRight, CheckCircle2 } from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import CTA from "@/components/CTA";
import { useI18n } from "@/i18n/I18nProvider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SUBJECT_ICONS = [GraduationCap, BookOpen, Code2, Wrench];

export default function ForStudentsPage() {
  const { t, lang } = useI18n();
  const subjects = ["standard", "advanced", "programming", "extras"];

  return (
    <>
      <PageHero
        eyebrow={t("forStudents.hero.eyebrow")}
        title={t("forStudents.hero.title")}
        description={t("forStudents.hero.description")}
      />

      {/* SUBJECTS */}
      <section data-testid="students-subjects" className="bg-brand-off py-20 md:py-28">
        <div className="container-x">
          <SectionHeading
            eyebrow={t("forStudents.subjects.eyebrow")}
            title={t("forStudents.subjects.title")}
            className="mb-12"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
            {subjects.map((s, i) => {
              const Icon = SUBJECT_ICONS[i];
              return (
                <article
                  key={s}
                  data-testid={`subject-${s}`}
                  className="group rounded-2xl bg-white border border-black/[0.06] p-8 soft-shadow soft-shadow-hover hover-lift"
                >
                  <div className="flex items-center justify-between mb-6">
                    <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-brand-paper border border-brand-line">
                      <Icon className="w-5 h-5 text-brand-charcoal" strokeWidth={1.4} />
                    </span>
                    <span className="text-[11px] tracking-[0.2em] uppercase text-brand-muted">
                      {t(`forStudents.subjects.${s}.label`)}
                    </span>
                  </div>
                  <h3 className="text-[24px] font-light tracking-tight text-brand-charcoal leading-tight">
                    {t(`forStudents.subjects.${s}.title`)}
                  </h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-brand-muted">
                    {t(`forStudents.subjects.${s}.desc`)}
                  </p>
                  <ul className="mt-6 space-y-2 text-[14.5px] text-brand-charcoal/90">
                    {(t(`forStudents.subjects.${s}.items`) || []).map((it, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <span className="mt-2 inline-block w-1.5 h-1.5 rounded-full bg-brand-blue" />
                        {it}
                      </li>
                    ))}
                  </ul>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* METHODOLOGY */}
      <section data-testid="students-method" className="bg-brand-paper py-20 md:py-28 border-t border-brand-line">
        <div className="container-x">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <p className="eyebrow mb-4">{t("forStudents.method.eyebrow")}</p>
              <h2 className="text-3xl md:text-[40px] font-light tracking-tight leading-[1.1] text-brand-charcoal">
                {t("forStudents.method.title")}
              </h2>
            </div>
            <div className="lg:col-span-7 lg:pt-3">
              <p className="text-[17px] leading-[1.7] text-brand-charcoal/90">
                {t("forStudents.method.paragraph1")}
              </p>
              <p className="mt-4 text-[16px] leading-[1.7] text-brand-muted">
                {t("forStudents.method.paragraph2")}
              </p>
            </div>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            {(t("forStudents.method.principles") || []).map((p, i) => (
              <div
                key={i}
                data-testid={`method-principle-${i + 1}`}
                className="rounded-2xl bg-white border border-black/[0.06] p-7 soft-shadow"
              >
                <span className="text-[11px] tracking-[0.2em] uppercase text-brand-muted">
                  0{i + 1}
                </span>
                <h3 className="mt-3 text-[19px] font-medium text-brand-charcoal leading-snug">{p.title}</h3>
                <p className="mt-2 text-[14.5px] text-brand-muted leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section data-testid="students-testimonials" className="bg-brand-off py-20 md:py-28">
        <div className="container-x">
          <SectionHeading
            eyebrow={t("forStudents.testimonials.eyebrow")}
            title={t("forStudents.testimonials.title")}
            className="mb-12"
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {(t("forStudents.testimonials.items") || []).map((it, i) => (
              <figure
                key={i}
                data-testid={`testimonial-${i + 1}`}
                className="rounded-2xl bg-white border border-black/[0.06] p-7 soft-shadow"
              >
                <blockquote className="text-[15.5px] leading-[1.65] text-brand-charcoal/95">
                  "{it.quote}"
                </blockquote>
                <figcaption className="mt-5 pt-4 border-t border-brand-line/80">
                  <div className="font-medium text-brand-charcoal text-[14px]">{it.name}</div>
                  <div className="text-[12.5px] text-brand-muted">{it.role}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING SUMMARY */}
      <section data-testid="students-pricing" className="bg-brand-paper py-20 md:py-28 border-t border-brand-line">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7">
            <p className="eyebrow mb-4">{t("forStudents.pricing_summary.eyebrow")}</p>
            <h2 className="text-3xl md:text-[40px] font-light tracking-tight leading-[1.1] text-brand-charcoal">
              {t("forStudents.pricing_summary.title")}
            </h2>
            <p className="mt-5 text-[16.5px] leading-relaxed text-brand-muted max-w-xl">
              {t("forStudents.pricing_summary.description")}
            </p>
          </div>
          <div className="lg:col-span-5 flex lg:justify-end">
            <CTA to="/pricing" variant="solid" testid="cta-see-pricing">
              {t("forStudents.pricing_summary.cta")}
            </CTA>
          </div>
        </div>
      </section>

      {/* AI IN LEARNING */}
      <section data-testid="students-ai" className="bg-brand-charcoal text-brand-off py-20 md:py-28">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-5">
            <p className="eyebrow-light mb-4">{t("forStudents.ai_in_learning.eyebrow")}</p>
            <h2 className="text-3xl md:text-[36px] font-light tracking-tight leading-[1.15] text-brand-off">
              {t("forStudents.ai_in_learning.title")}
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-3">
            <p className="text-[17px] leading-[1.75] text-white/80">
              {t("forStudents.ai_in_learning.paragraph")}
            </p>
          </div>
        </div>
      </section>

      {/* REQUEST FORM */}
      <section data-testid="students-form" className="bg-brand-off py-20 md:py-28">
        <div className="container-x">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <SectionHeading
                eyebrow={t("forStudents.cta.title")}
                title={t("forStudentsForm.title")}
                description={t("forStudentsForm.description")}
              />
            </div>
            <div className="lg:col-span-7">
              <LessonRequestForm lang={lang} t={t} />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function LessonRequestForm({ lang, t }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    level: "",
    pack: "",
    language: "",
    schedule: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  const validate = () => {
    if (!form.name.trim()) return t("contact.errors.name");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return t("contact.errors.email");
    if (!form.subject.trim()) return t("audit.errors.pickOne");
    if (!form.level) return t("audit.errors.pickOne");
    if (!form.pack) return t("audit.errors.pickOne");
    if (!form.language) return t("audit.errors.pickOne");
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, lang };
      const res = await axios.post(`${API}/lesson-request`, payload);
      setSuccess(true);
      toast.success(
        res.data?.email_sent
          ? t("forStudentsForm.toast.sent")
          : t("contact.toast.received"),
      );
      setForm({
        name: "",
        email: "",
        subject: "",
        level: "",
        pack: "",
        language: "",
        schedule: "",
        message: "",
      });
    } catch (e2) {
      console.error(e2);
      toast.error(
        e2?.response?.data?.detail || t("contact.errors.generic"),
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div
        data-testid="lesson-request-success"
        className="rounded-2xl bg-white border border-black/[0.06] p-10 soft-shadow text-center"
      >
        <CheckCircle2 className="w-10 h-10 text-brand-blue mx-auto" strokeWidth={1.5} />
        <h3 className="mt-6 text-3xl font-light tracking-tight text-brand-charcoal">
          {t("forStudentsForm.success.title")}
        </h3>
        <p className="mt-3 text-brand-muted max-w-md mx-auto">
          {t("forStudentsForm.success.description")}
        </p>
        <button
          type="button"
          onClick={() => setSuccess(false)}
          data-testid="lesson-request-another"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-brand-off px-5 py-3 text-[13.5px] font-medium hover:bg-brand-dark transition-colors"
        >
          {t("forStudentsForm.success.another")}
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid="lesson-request-form"
      className="rounded-2xl bg-white border border-black/[0.06] p-8 md:p-10 soft-shadow grid grid-cols-1 gap-6"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Field label={t("forStudentsForm.fields.name")} required>
          <input
            type="text"
            value={form.name}
            onChange={update("name")}
            data-testid="lr-input-name"
            className={inputCls}
            maxLength={200}
          />
        </Field>
        <Field label={t("forStudentsForm.fields.email")} required>
          <input
            type="email"
            value={form.email}
            onChange={update("email")}
            data-testid="lr-input-email"
            className={inputCls}
          />
        </Field>
      </div>

      <Field label={t("forStudentsForm.fields.subject")} required>
        <input
          type="text"
          value={form.subject}
          onChange={update("subject")}
          placeholder={t("forStudentsForm.subjectsHint")}
          data-testid="lr-input-subject"
          className={inputCls}
        />
      </Field>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Field label={t("forStudentsForm.fields.level")} required>
          <Select value={form.level} onValueChange={(v) => setForm((f) => ({ ...f, level: v }))}>
            <SelectTrigger data-testid="lr-input-level" className={selectCls}>
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              {["standard", "advanced", "programming", "extras"].map((k) => (
                <SelectItem key={k} value={k} data-testid={`lr-level-${k}`}>
                  {t(`forStudentsForm.levels.${k}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label={t("forStudentsForm.fields.pack")} required>
          <Select value={form.pack} onValueChange={(v) => setForm((f) => ({ ...f, pack: v }))}>
            <SelectTrigger data-testid="lr-input-pack" className={selectCls}>
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              {["single", "four", "ongoing"].map((k) => (
                <SelectItem key={k} value={k} data-testid={`lr-pack-${k}`}>
                  {t(`forStudentsForm.packs.${k}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label={t("forStudentsForm.fields.language")} required>
          <Select value={form.language} onValueChange={(v) => setForm((f) => ({ ...f, language: v }))}>
            <SelectTrigger data-testid="lr-input-language" className={selectCls}>
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              {["lv", "en"].map((k) => (
                <SelectItem key={k} value={k} data-testid={`lr-lang-${k}`}>
                  {t(`forStudentsForm.languages.${k}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <Field label={t("forStudentsForm.fields.schedule")}>
        <input
          type="text"
          value={form.schedule}
          onChange={update("schedule")}
          placeholder={t("forStudentsForm.schedulePlaceholder")}
          data-testid="lr-input-schedule"
          className={inputCls}
        />
      </Field>

      <Field label={t("forStudentsForm.fields.message")}>
        <textarea
          value={form.message}
          onChange={update("message")}
          rows={4}
          data-testid="lr-input-message"
          className={`${inputCls} min-h-[110px] resize-y leading-relaxed`}
          maxLength={3000}
        />
      </Field>

      <div className="flex justify-end pt-2">
        <button
          type="submit"
          disabled={submitting}
          data-testid="lr-submit"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-charcoal text-brand-off px-6 py-3.5 text-[14px] font-medium hover:bg-brand-dark transition-colors disabled:opacity-50"
        >
          {submitting ? t("common.sending") : t("forStudentsForm.fields.submit")}
          {!submitting && <ArrowRight className="w-3.5 h-3.5" />}
        </button>
      </div>
    </form>
  );
}

const inputCls =
  "w-full h-12 rounded-xl border border-brand-line bg-brand-paper px-4 text-[15px] text-brand-charcoal placeholder:text-brand-muted focus:outline-none focus:border-brand-charcoal/40 focus:ring-2 focus:ring-brand-blue/20 transition-colors";
const selectCls =
  "h-12 rounded-xl border-brand-line bg-brand-paper px-4 text-[15px] data-[placeholder]:text-brand-muted focus:ring-2 focus:ring-brand-blue/30";

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block mb-2 text-[12.5px] uppercase tracking-[0.16em] text-brand-muted font-medium">
        {label} {required && <span className="text-brand-blue">*</span>}
      </span>
      {children}
    </label>
  );
}
